import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import useFormValidation from '../hooks/useFormValidation';

import AuthLayout from '../components/AuthLayout';

import Button from '../components/inputs/Button';
import LoadingSpinner from '../components/LoadingSpinner';

import { validateAgreeToTermsField } from '../helpers/validators';
import CheckboxInput from '../components/inputs/CheckboxInput';
import ExternalThemeLink from '../components/ExternalThemeLink';

export default function AgreeToTerms() {
  const { agreetoterms, user } = useAuth();
  const location = useLocation();
  const email = location.state?.email;
  const navigate = useNavigate();

  // If user is not authenticated, redirect to sign in
  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} />;
  }

  const [formState, setFormState] = useState({
    agreeToTerms: '',
  });
  const [error, setError] = useState();
  const [agreeing, setAgreeing] = useState(false);
  const [agreeSuccessful, setAgreeSuccessful] = useState();

  // eslint-disable-next-line no-unused-vars
  const [invalidFields, _, __, validateForm] = useFormValidation([
    { name: 'agreeToTerms', validator: validateAgreeToTermsField },
  ]);

  function handleChangeAgreeToTerms() {
    setFormState((prevState) => ({
      ...prevState,
      agreeToTerms: !prevState.agreeToTerms,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (validateForm(formState, setError)) {
      setAgreeing(true);
      agreetoterms({ email, ...formState })
        .then(() => {
          setAgreeSuccessful(true);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setAgreeing(false);
        });
    }
  }

  useEffect(() => {
    if (!agreeing && agreeSuccessful) {
      navigate('/signin', { state: { email } });
    }
  }, [agreeSuccessful, agreeing, email, navigate]);

  return (
    <AuthLayout
      heading={
        <h3>
          You haven't agree to our Terms of Use.{' '}
          <span className="text-secondary">Please read and accept.</span>
        </h3>
      }
    >
      {agreeing && <LoadingSpinner message="Submitting..." />}
      {!agreeing && agreeSuccessful == null && (
        <form className="flex flex-col text-center" onSubmit={handleSubmit}>
          <h4>Agree to Terms of Use</h4>
          <p className="mb-1">
            It looks like you haven't agreed to our Terms of Use.
          </p>
          <p className="mb-6">Please read and accept.</p>
          <div className="flex justify-center">
            <button
              className="flex flex-row justify-center"
              type="button"
              onClick={handleChangeAgreeToTerms}
            >
              <CheckboxInput
                className="mr-2"
                checked={formState.agreeToTerms}
              />{' '}
              I agree to the{' '}
            </button>
            <ExternalThemeLink
              className="ml-1"
              href="/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </ExternalThemeLink>
          </div>
          {error && (
            <div className="mx-auto text-error mt-3 -mb-9">{error}</div>
          )}
          <Button
            className="mt-14 w-80 mx-auto"
            type="submit"
            primary
            disabled={invalidFields.length > 0}
          >
            Agree
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
